import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { Sport } from '__generated__/globalTypes';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import DotsLoader from 'atoms/loader/DotsLoader';
import { BodyM, BodyS } from 'atoms/typography';
import { RefereeProgression } from 'components/referral/RefereeProgression';
import { messages } from 'components/referral/messages';
import Avatar from 'components/user/Avatar';
import { MyClubLink } from 'components/user/MyClubLink';
import UserName from 'components/user/UserName';
import { useCurrentUserContext } from 'contexts/currentUser';
import TimeLeft from 'contexts/ticker/TimeLeft';
import { isType } from 'gql';
import { useReferralProgramConfig } from 'hooks/useReferralProgramConfig';
import { CARDS_REQUIREMENTS_BY_SPORT } from 'lib/referral';

import { InProgressReferralBar_Referral } from './__generated__/index.graphql';

type Props = {
  referee: InProgressReferralBar_Referral;
  sport: Sport;
};

export const InProgressReferralBar = ({ referee, sport }: Props) => {
  const { fiatCurrency } = useCurrentUserContext();
  const { formatNumber } = useIntl();
  const { userFiatAmount: referralProgramConfigFiatAmount } =
    useReferralProgramConfig(sport);

  const { referrer } = referee;
  const completed =
    referee.refereeSportCardsBoughtFromPrimaryMarketCount >=
    CARDS_REQUIREMENTS_BY_SPORT[sport];

  return (
    <Vertical gap={2}>
      <SBHorizontal>
        <BodyM bold>
          <FormattedMessage
            id="InProgressReferralBar.title"
            defaultMessage="Your progression"
          />
        </BodyM>
        {!completed && (
          <TimeLeft time={referee.expirationDate} withExplicitTime />
        )}
      </SBHorizontal>
      <BodyS as="p">
        <FormattedMessage
          {...(completed ? messages.claimSoon : messages.title)}
          values={{
            amount:
              referralProgramConfigFiatAmount === undefined ? (
                <DotsLoader small />
              ) : (
                formatNumber(referralProgramConfigFiatAmount, {
                  style: 'currency',
                  currency: fiatCurrency.code,
                  maximumFractionDigits: 0,
                })
              ),
          }}
        />
      </BodyS>
      <RefereeProgression referee={referee} sport={sport} />
      <BodyM as="p" color="var(--c-neutral-600)">
        {isType(referrer, 'User') ? (
          <Horizontal as="span">
            <FormattedMessage
              id="InProgressReferralBar.referredBy"
              defaultMessage="Referred by {nickname}"
              values={{
                nickname: (
                  <MyClubLink user={referrer}>
                    <Horizontal as="span">
                      <Avatar user={referrer} rounded size={3} />
                      <UserName user={referrer} />
                    </Horizontal>
                  </MyClubLink>
                ),
              }}
            />
          </Horizontal>
        ) : (
          <FormattedMessage {...messages.invitedByAnAffiliate} />
        )}
      </BodyM>
    </Vertical>
  );
};

InProgressReferralBar.fragments = {
  referral: gql`
    fragment InProgressReferralBar_Referral on Referral {
      id
      expirationDate
      referrer {
        ... on User {
          slug
          ...UserName_publicUserInfoInterface
          ...Avatar_user
        }
        ... on UserSource {
          id
          name
        }
      }
      ...RefereeProgression_Referral
    }
    ${UserName.fragments.user}
    ${Avatar.fragments.user}
    ${RefereeProgression.fragments.referral}
  ` as TypedDocumentNode<InProgressReferralBar_Referral>,
};
